import { IAPIRoutes } from "../../../../config";

export type IBillingAdminRepository = ReturnType<
  typeof BillingAdminRepositoryFactory
>;

export const BillingAdminRepositoryFactory = ({
  APIProvider,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, PUT, POST, DELETE } = APIProvider;

  /**
   * Payers
   */
  const GetPayers: IGetBillingAdminPayers = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingPayers, params) as Promise<
      PaginatedListResponse<IGetBillingAdminPayersResponse>
    >;
  };

  const UpdatePayer: IUpdateBillingAdminPayer = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingPayer.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreatePayer: ICreateBillingAdminPayer = async (payload) => {
    return POST(APIRoutes.apiCreateBillingPayer, payload);
  };

  const DeletePayer: IDeleteBillingAdminPayer = async (id) => {
    return DELETE(APIRoutes.apiDeleteBillingPayer.replace("{id}", String(id)));
  };

  /**
   * Providers
   */
  const GetProviders: IGetBillingAdminProviders = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingProviders, params) as Promise<
      PaginatedListResponse<IGetBillingAdminProvidersResponse>
    >;
  };

  const UpdateProvider: IUpdateBillingAdminProvider = async (payload) => {
    const { id, created_at, updated_at, clinics, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingProvider.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateProvider: ICreateBillingAdminProvider = async (payload) => {
    return POST(APIRoutes.apiCreateBillingProvider, payload);
  };

  const DeleteProvider: IDeleteBillingAdminProvider = async (id) => {
    return DELETE(
      APIRoutes.apiDeleteBillingProvider.replace("{id}", String(id))
    );
  };

  /**
   * Users
   */
  const GetUsers: IGetBillingAdminUsers = async (params) => {
    const query = { ...params };
    if ("apiName" in query) {
      delete query.apiName;
    }
    return GET(APIRoutes.apiGetBillingUsers, query) as Promise<
      PaginatedListResponse<IGetBillingAdminUsersResponse>
    >;
  };

  /**
   * Entity
   */
  const GetEntities: IGetBillingAdminEntity = async (payload) => {
    const params = payload;
    return GET(APIRoutes.apiGetBillingEntity, params) as Promise<{
      success: boolean;
      data: IGetBillingAdminEntityResponse;
    }>;
  };

  const UpdateEntity: IUpdateBillingAdminEntity = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingEntity.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateEntity: ICreateBillingAdminEntity = async (payload) => {
    return POST(APIRoutes.apiCreateBillingEntity, payload);
  };

  const DeleteEntity: IDeleteBillingAdminEntity = async (id) => {
    return DELETE(APIRoutes.apiDeleteBillingEntity.replace("{id}", String(id)));
  };

  /**
   * Clinics
   */
  const GetClinics: IGetBillingAdminClinics = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingClinics, params) as Promise<
      PaginatedListResponse<IGetBillingAdminClinicsResponse>
    >;
  };

  const UpdateClinic: IUpdateBillingAdminClinic = async (payload) => {
    const { id, ps_pos, billing_processor, billing_type } = payload;

    const body: {
      ps_pos?: string | null;
      billing_processor?: string | null;
      billing_type?: number | null;
    } = {};

    if (ps_pos !== undefined) {
      body.ps_pos = ps_pos === "" ? null : ps_pos;
    }

    if (billing_processor !== undefined) {
      body.billing_processor =
        billing_processor === null ||
        billing_processor === "" ||
        billing_processor.toUpperCase() === "PS"
          ? null
          : billing_processor;
    }

    if (billing_type !== undefined) {
      body.billing_type = billing_type;
    }

    return PUT(
      APIRoutes.apiUpdateBillingClinic.replace("{clinic_id}", String(id)),
      body
    );
  };

  /**
   * Rosters
   */
  const GetRosters: IGetBillingAdminRoster = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingRosters, params) as Promise<
      PaginatedListResponse<IGetBillingAdminRosterResponse>
    >;
  };

  const UpdateRoster: IUpdateBillingAdminRoster = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingRoster.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateRoster: ICreateBillingAdminRoster = async (payload) => {
    return POST(APIRoutes.apiCreateBillingRoster, payload);
  };
  const DeleteRoster: IDeleteBillingAdminRoster = async (id) => {
    return DELETE(APIRoutes.apiDeleteBillingRoster.replace("{id}", String(id)));
  };

  /**
   * Patients
   */
  const GetPatients: IGetBillingAdminPatients = async (params) => {
    const query = { ...params };
    if ("apiName" in query) {
      delete query.apiName;
    }
    return GET(APIRoutes.apiGetBillingPatients, query) as Promise<
      PaginatedListResponse<IGetBillingAdminPatientsResponse>
    >;
  };

  const UpdatePatient: IUpdateBillingAdminPatient = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingPatient.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreatePatient: ICreateBillingAdminPatient = async (payload) => {
    return POST(APIRoutes.apiCreateBillingPatient, payload);
  };

  const DeletePatient: IDeleteBillingAdminPatient = async (id) => {
    return DELETE(
      APIRoutes.apiDeleteBillingPatient.replace("{id}", String(id))
    );
  };

  /**
   * Charges
   */
  const GetCharges: IGetBillingAdminCharges = async (params) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingCharges, params) as Promise<
      PaginatedListResponse<IGetBillingAdminChargesResponse>
    >;
  };

  const UpdateCharges: IUpdateBillingAdminCharges = async (payload) => {
    const { id, created_at, updated_at, ...bodyData } = payload;

    return PUT(
      APIRoutes.apiUpdateBillingCharges.replace("{id}", String(id)),
      bodyData
    );
  };

  const CreateCharges: ICreateBillingAdminCharges = async (payload) => {
    return POST(APIRoutes.apiCreateBillingCharges, payload);
  };

  const DeleteCharges: IDeleteBillingAdminCharges = async (id) => {
    return DELETE(
      APIRoutes.apiDeleteBillingCharges.replace("{id}", String(id))
    );
  };

  const GetChargesStatusSummary = async (params: any) => {
    if ("apiName" in params) {
      delete params.apiName;
    }
    return GET(APIRoutes.apiGetBillingChargesStatusSummary, params) as Promise<{
      success: boolean;
      data: Array<{ status: statusType; count: number }>;
    }>;
  };

  /**
   * RTX eligibility: GET /rtx/eligibility/{patient_id}?dos_from=&dos_to=
   */
  const GetEligibility: IGetRtxEligibility = async (payload) => {
    const path = APIRoutes.apiGetPatientEligibility.replace(
      "{patient_id}",
      String(payload.patientId)
    );
    const params: Record<string, string> = {};
    if (payload.dos_from != null) params.dos_from = payload.dos_from;
    if (payload.dos_to != null) params.dos_to = payload.dos_to;
    return GET(
      path,
      Object.keys(params).length ? params : undefined
    ) as ReturnType<IGetRtxEligibility>;
  };

  const RefreshEligibility: IRefreshEligibility = async (payload) => {
    const path = APIRoutes.apiRefreshEligibility
      .replace("{patient_id}", String(payload.patientId))
      .replace("{eligibility_id}", String(payload.eligibilityId));
    const params: Record<string, string> = {};
    if (payload.payerCode != null) {
      params.payer_code = payload.payerCode;
    }
    return GET(
      path,
      Object.keys(params).length ? params : undefined
    ) as ReturnType<IRefreshEligibility>;
  };

  return {
    GetPayers,
    UpdatePayer,
    CreatePayer,
    DeletePayer,

    GetProviders,
    UpdateProvider,
    CreateProvider,
    DeleteProvider,

    GetUsers,

    GetEntities,
    UpdateEntity,
    CreateEntity,
    DeleteEntity,

    GetClinics,
    UpdateClinic,

    GetRosters,
    UpdateRoster,
    CreateRoster,
    DeleteRoster,

    GetPatients,
    UpdatePatient,
    CreatePatient,
    DeletePatient,

    GetCharges,
    UpdateCharges,
    CreateCharges,
    DeleteCharges,
    GetChargesStatusSummary,
    GetEligibility,
    RefreshEligibility,
  };
};
